/**
 * useReactionSummaries — récupère en UN SEUL appel le détail des réactions
 * (J'aime / Bravo / Idée / Coup de cœur) pour tous les posts du fil.
 *
 * Même logique que useAuthors : on ne redemande que les post_id inconnus,
 * et le cache est conservé entre les pages du fil (« Charger plus »).
 *
 * L'endpoint est plafonné à 60 identifiants côté backend ; on découpe donc
 * en lots de 50 pour garder une marge et borner la longueur d'URL.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const BATCH_SIZE = 50;

export function useReactionSummaries(postIds) {
  const [summaries, setSummaries] = useState({});
  const requested = useRef(new Set());

  const key = Array.isArray(postIds) ? postIds.filter(Boolean).join(",") : "";

  useEffect(() => {
    const ids = key ? key.split(",") : [];
    const missing = ids.filter((id) => !requested.current.has(id));
    if (missing.length === 0) return undefined;

    missing.forEach((id) => requested.current.add(id));

    let alive = true;
    const batches = [];
    for (let i = 0; i < missing.length; i += BATCH_SIZE) {
      batches.push(missing.slice(i, i + BATCH_SIZE));
    }

    Promise.all(
      batches.map((batch) =>
        api
          .get(`/posts/reactions/summary?post_ids=${batch.join(",")}`)
          .then((r) => r.data)
          .catch(() => ({})) // un échec de comptage ne doit jamais casser le fil
      )
    ).then((results) => {
      if (!alive) return;
      const merged = results.reduce((acc, r) => ({ ...acc, ...r }), {});
      if (Object.keys(merged).length) {
        setSummaries((prev) => ({ ...prev, ...merged }));
      }
    });

    return () => {
      alive = false;
    };
  }, [key]);

  return summaries;
}

export default useReactionSummaries;
